import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Sparkles, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/use-auth';
import type { AdminUserSummary } from '../admin/types';
import {
  formatHm,
  type OvertimeHistoryEntry,
  type PendingOvertimeWorkday,
} from './types';

/**
 * Panel "Horas a compensar" en /solicitudes. Reúne:
 *   - Botón de HR/SUPER para añadir un ajuste manual de saldo.
 *   - Jornadas con horas a compensar PENDIENTES de decidir (accept/reject inline).
 *   - Histórico unificado: jornadas APROBADAS + ajustes manuales (badge).
 *
 * Las jornadas REJECTED no se muestran aquí — el balance del día sigue
 * visible en el resumen pero no se les hace seguimiento.
 */
export function OvertimeApprovalPanel() {
  const { user } = useAuth();
  const isHr = user?.role === 'HR_ADMIN' || user?.role === 'SUPER_ADMIN';
  const [adjustOpen, setAdjustOpen] = useState(false);

  const pending = useQuery({
    queryKey: ['overtime', 'workday', 'pending'],
    queryFn: () => api<PendingOvertimeWorkday[]>('/overtime/workday/pending'),
  });
  const history = useQuery({
    queryKey: ['overtime', 'workday', 'history'],
    queryFn: () => api<OvertimeHistoryEntry[]>('/overtime/workday/history'),
  });

  return (
    <div className="space-y-7">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setAdjustOpen(true)}
          disabled={!user}
        >
          <Plus className="h-3.5 w-3.5" />
          Añadir horas a compensar
        </Button>
      </div>

      <PendingSection items={pending.data ?? []} loading={pending.isLoading} />
      <HistorySection items={history.data ?? []} loading={history.isLoading} />

      {adjustOpen && user ? (
        <AddAdjustmentModal
          actorId={user.id}
          actorName={user.fullName}
          isHr={isHr}
          onClose={() => setAdjustOpen(false)}
        />
      ) : null}
    </div>
  );
}

function PendingSection({
  items,
  loading,
}: {
  items: PendingOvertimeWorkday[];
  loading: boolean;
}) {
  return (
    <section>
      <Eyebrow>Pendientes de decidir</Eyebrow>
      {loading ? (
        <p className="mt-3 text-[13px] text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-[13px] text-muted-foreground">
          No tienes horas a compensar pendientes de aprobar.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {items.map((w) => (
            <PendingRow key={w.workdayId} workday={w} />
          ))}
        </ul>
      )}
    </section>
  );
}

function PendingRow({ workday }: { workday: PendingOvertimeWorkday }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'idle' | 'accepting'>('idle');
  const [notes, setNotes] = useState('');

  const approveMutation = useMutation({
    mutationFn: () =>
      api(`/overtime/workday/${workday.workdayId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes.trim() }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
    },
  });
  const rejectMutation = useMutation({
    mutationFn: () =>
      api(`/overtime/workday/${workday.workdayId}/reject`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
    },
  });

  const pending = approveMutation.isPending || rejectMutation.isPending;
  const error = approveMutation.error ?? rejectMutation.error;
  const errorMessage = error
    ? error instanceof ApiError
      ? error.message
      : 'Error al guardar la decisión'
    : null;

  return (
    <li className="rounded-sm border border-amber-300/40 bg-amber-50/40 px-5 py-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-[14px] tabular-nums">{workday.date}</p>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Trabajado {formatHm(workday.workedMinutes)} / esperado{' '}
            {formatHm(workday.expectedMinutes)} ·{' '}
            <span className="text-amber-700 font-medium">
              +{formatHm(workday.overtimeMinutes)} a compensar
            </span>
          </p>
        </div>
        {mode === 'idle' ? (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => rejectMutation.mutate()}
              disabled={pending}
            >
              <X className="h-3.5 w-3.5" />
              Rechazar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setMode('accepting')}
              disabled={pending}
            >
              <Check className="h-3.5 w-3.5" />
              Aceptar
            </Button>
          </div>
        ) : null}
      </div>

      {mode === 'accepting' ? (
        <div className="mt-3 flex flex-col gap-2">
          <Label htmlFor={`notes-${workday.workdayId}`}>
            Motivo <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id={`notes-${workday.workdayId}`}
            rows={3}
            maxLength={500}
            placeholder="Ej. Cierre de proyecto X, urgencia de cliente…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMode('idle');
                setNotes('');
                approveMutation.reset();
              }}
              disabled={pending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={pending || notes.trim().length === 0}
              onClick={() => approveMutation.mutate()}
            >
              {approveMutation.isPending ? 'Guardando…' : 'Confirmar y aceptar'}
            </Button>
          </div>
        </div>
      ) : null}

      {errorMessage ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}
    </li>
  );
}

function HistorySection({
  items,
  loading,
}: {
  items: OvertimeHistoryEntry[];
  loading: boolean;
}) {
  return (
    <section>
      <Eyebrow>Histórico aprobado</Eyebrow>
      {loading ? (
        <p className="mt-3 text-[13px] text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-[13px] text-muted-foreground">
          Cuando apruebes horas a compensar (o RR. HH. añada un ajuste) aparecerán aquí.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border rounded-sm border border-border bg-paper">
          {items.map((w) => (
            <HistoryRow key={`${w.kind}-${w.id}`} entry={w} />
          ))}
        </ul>
      )}
    </section>
  );
}

function HistoryRow({ entry }: { entry: OvertimeHistoryEntry }) {
  const isManual = entry.kind === 'MANUAL_ADJUSTMENT';
  // Los ajustes manuales de RR. HH. pueden restar horas (minutos negativos):
  // mostramos el signo y el color según sume o reste.
  const isNegative = entry.overtimeMinutes < 0;
  return (
    <li className="px-5 py-3.5">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-2">
          <p className="font-mono text-[14px] tabular-nums">{entry.date}</p>
          {isManual ? (
            <span
              className="inline-flex items-center font-mono text-[9px] font-medium uppercase border border-indigo-500/40 bg-indigo-50 text-indigo-700 rounded-[3px] px-1.5 py-0.5"
              style={{ letterSpacing: '0.06em' }}
            >
              <Sparkles className="h-2.5 w-2.5 mr-1" />
              Ajuste RR. HH.
            </span>
          ) : null}
        </div>
        <p
          className={`text-[13px] font-medium ${isNegative ? 'text-destructive' : 'text-success'}`}
        >
          {isNegative ? '−' : '+'}
          {formatHm(Math.abs(entry.overtimeMinutes))}
        </p>
      </div>
      {isManual ? (
        <p className="text-[12px] text-muted-foreground mt-0.5">
          {entry.createdByName ? `Añadido por ${entry.createdByName}` : 'Ajuste manual'}
          {entry.decidedAt ? ` · ${formatDecidedDate(entry.decidedAt)}` : ''}
        </p>
      ) : (
        <p className="text-[12px] text-muted-foreground mt-0.5">
          Trabajado {formatHm(entry.workedMinutes ?? 0)} / esperado{' '}
          {formatHm(entry.expectedMinutes ?? 0)}
          {entry.decidedAt ? ` · aprobado ${formatDecidedDate(entry.decidedAt)}` : ''}
        </p>
      )}
      {entry.notes ? (
        <p className="mt-2 text-[13px] text-foreground whitespace-pre-line">{entry.notes}</p>
      ) : null}
    </li>
  );
}

function AddAdjustmentModal({
  actorId,
  actorName,
  isHr,
  onClose,
}: {
  actorId: string;
  actorName: string;
  isHr: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  // Empleado por defecto: el actor (todos los empleados pueden ajustarse a sí
  // mismos). HR/SUPER puede cambiarlo desde el selector.
  const [userId, setUserId] = useState(actorId);
  const [hours, setHours] = useState('1');
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [reason, setReason] = useState('');

  const users = useQuery({
    queryKey: ['admin', 'users', { includeInactive: false }],
    queryFn: () => api<AdminUserSummary[]>('/admin/users'),
    enabled: isHr,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const minutes = Math.round(parseFloat(hours.replace(',', '.')) * 60);
      return api('/overtime/adjustments', {
        method: 'POST',
        body: JSON.stringify({
          userId,
          minutes,
          effectiveDate,
          reason: reason.trim(),
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      onClose();
    },
  });

  const hoursValue = parseFloat(hours.replace(',', '.'));
  const isValid =
    userId.length > 0 &&
    Number.isFinite(hoursValue) &&
    hoursValue >= 0.25 &&
    hoursValue <= 24 &&
    reason.trim().length >= 3 &&
    effectiveDate.length === 10;

  const errorMessage = mutation.error
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Error al guardar el ajuste'
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="w-full max-w-md bg-card border border-border rounded-sm shadow-xl max-h-[95dvh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <h2 className="text-base font-semibold">Añadir horas a compensar</h2>
          <button
            onClick={onClose}
            aria-label="Cancelar"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <form
          className="p-5 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid && !mutation.isPending) mutation.mutate();
          }}
        >
          {isHr ? (
            <div className="space-y-1.5">
              <Label htmlFor="adj-user">
                Empleado <span className="text-destructive">*</span>
              </Label>
              <select
                id="adj-user"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="flex h-9 w-full rounded-sm border border-line bg-card px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-icaria-600/40"
              >
                <option value="">Selecciona un empleado…</option>
                {(users.data ?? [])
                  .filter((u) => u.isActive)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName}
                      {u.id === actorId ? ' (tú)' : ''}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Empleado</Label>
              <p className="rounded-sm border border-line bg-paper px-3 py-2 text-sm text-foreground">
                {actorName} <span className="text-muted-foreground">(tú)</span>
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="adj-date">
                Día <span className="text-destructive">*</span>
              </Label>
              <Input
                id="adj-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                max={today}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adj-hours">
                Horas <span className="text-destructive">*</span>
              </Label>
              <Input
                id="adj-hours"
                inputMode="decimal"
                step="0.25"
                type="number"
                min="0.25"
                max="24"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adj-reason">
              Motivo <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="adj-reason"
              rows={3}
              maxLength={500}
              placeholder="Ej. Compensación cierre de proyecto, evento fuera de horario…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
            <p
              className={cn(
                'text-[11px]',
                reason.trim().length < 3
                  ? 'text-destructive'
                  : 'text-muted-foreground',
              )}
            >
              {reason.trim().length}/500 · mínimo 3 caracteres
            </p>
          </div>

          {errorMessage ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!isValid || mutation.isPending}
            >
              {mutation.isPending ? 'Guardando…' : 'Añadir horas a compensar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p
      className={cn(
        'font-mono text-[11px] font-medium uppercase text-muted-foreground',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </p>
  );
}

function formatDecidedDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}
